import { useEffect, useRef, useState } from "react";

import {
  EFFORT_OPTS as EFFORT_VALUES,
  MODEL_OPTS as MODEL_VALUES,
} from "@tinsmith/model-options";

import { AgentCredentials } from "./AgentCredentials";
import { Billing } from "./Billing";
import { Connections } from "./Connections";
import { EmailChange } from "./EmailChange";
import { Jobs } from "./Jobs";
import { Notifications } from "./Notifications";
import { SaveBar, SettingsRow } from "./parts";
import { Runners } from "./Runners";
import { Security } from "./Security";
import styles from "./Settings.module.css";
import { Team } from "./Team";
import { useSession } from "../../auth/session";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { Avatar } from "../../components/Avatar";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { api } from "../../data/api/client";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import {
  updateWorkspaceSettings,
  useWorkspaceSettings,
  type WorkspaceSettings,
} from "../../data/api/workspace";

const MODEL_OPTS = MODEL_VALUES.map((opt) => ({
  label: opt.value,
  value: opt.value,
}));
const EFFORT_OPTS = EFFORT_VALUES.map((opt) => ({
  label: opt.value,
  value: opt.value,
}));
export const Settings = () => {
  const { refresh, user } = useSession();
  const { workspace } = useCurrentWorkspace();
  const wsId = workspace?.public_id ?? null;
  const canEditWs = workspace?.role === "admin" || workspace?.role === "owner";
  const { refetch: refetchWs, settings: wsSaved } = useWorkspaceSettings(wsId);

  const [wsForm, setWsForm] = useState<WorkspaceSettings | null>(null);
  // Null = "track the loaded user"; a string = an unsaved local edit.
  const [nameOverride, setNameOverride] = useState<string | null>(null);
  const [flash, setFlash] = useState(false);
  const [busy, setBusy] = useState(false);
  const wsSeeded = useRef(false);
  useDocumentTitle("Settings");

  // Seed the workspace-settings form from the server once it loads.
  useEffect(() => {
    if (wsSaved && !wsSeeded.current) {
      wsSeeded.current = true;
      setWsForm(wsSaved);
    }
  }, [wsSaved]);

  const name = nameOverride ?? user?.display_name ?? "";
  const wsSet = <K extends keyof WorkspaceSettings>(
    key: K,
    value: WorkspaceSettings[K],
  ): void => setWsForm((f) => (f ? { ...f, [key]: value } : f));
  const wsDirty =
    wsForm !== null &&
    wsSaved !== null &&
    JSON.stringify(wsForm) !== JSON.stringify(wsSaved);
  const nameDirty =
    nameOverride !== null &&
    nameOverride.trim().length > 0 &&
    nameOverride.trim() !== (user?.display_name ?? "");
  const dirty = wsDirty || nameDirty;

  const discard = (): void => {
    if (wsSaved) {
      setWsForm(wsSaved);
    }
    setNameOverride(null);
  };

  const save = (): void => {
    if (busy) {
      return;
    }
    const finish = (): void => {
      setFlash(true);
      setTimeout(() => setFlash(false), 1800);
    };
    const tasks: Array<Promise<unknown>> = [];
    if (wsDirty && wsId && wsForm && wsSaved) {
      // Send only the changed workspace-settings fields.
      const patch: Partial<WorkspaceSettings> = {};
      (Object.keys(wsForm) as Array<keyof WorkspaceSettings>).forEach((k) => {
        if (wsForm[k] !== wsSaved[k]) {
          (patch as Record<string, unknown>)[k] = wsForm[k];
        }
      });
      tasks.push(updateWorkspaceSettings(wsId, patch).then(() => refetchWs()));
    }
    if (nameDirty && nameOverride !== null) {
      tasks.push(
        api.patch("/me", { display_name: nameOverride.trim() }).then(() => {
          setNameOverride(null);
          return refresh();
        }),
      );
    }
    if (tasks.length === 0) {
      finish();
      return;
    }
    setBusy(true);
    void Promise.all(tasks)
      .catch(() => undefined)
      .finally(() => {
        setBusy(false);
        finish();
      });
  };

  return (
    <div className={styles.settings}>
      <div className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
      </div>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Profile</h3>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.fixedLabel}>
              <div className={styles.label}>Display name</div>
            </div>
            <Input
              onChange={(e) => setNameOverride(e.target.value)}
              style={{ flex: 1 }}
              value={name}
            />
          </div>
          <div className={styles.row}>
            <div className={styles.fixedLabel}>
              <div className={styles.label}>Email</div>
              <div className={styles.help}>
                Used for sign-in and notifications.
              </div>
            </div>
            <EmailChange email={user?.email ?? ""} />
          </div>
          <div className={styles.row}>
            <div className={styles.fixedLabel}>
              <div className={styles.label}>Avatar</div>
              <div className={styles.help}>Auto-generated from your name.</div>
            </div>
            <Avatar name={name || user?.display_name || "?"} size={36} />
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Runtime defaults</h3>
        <div className={styles.card}>
          <SettingsRow
            help="Most tasks; you can pick another at launch."
            label="Default model"
          >
            <Select
              disabled={!canEditWs || !wsForm}
              onValueChange={(v) => wsSet("default_model", v)}
              options={MODEL_OPTS}
              value={wsForm?.default_model ?? ""}
            />
          </SettingsRow>
          <SettingsRow
            help="How much Claude reasons before responding; you can pick another at launch."
            label="Default effort"
          >
            <Select
              disabled={!canEditWs || !wsForm}
              onValueChange={(v) =>
                wsSet(
                  "default_effort",
                  v as WorkspaceSettings["default_effort"],
                )
              }
              options={EFFORT_OPTS}
              value={wsForm?.default_effort ?? ""}
            />
          </SettingsRow>
        </div>
      </section>

      <AgentCredentials />

      <Team />

      <Runners />

      <Billing />

      <Connections />

      <Security />

      <Notifications />

      <Jobs staff={user?.staff ?? false} />

      <SaveBar dirty={dirty} flash={flash} onDiscard={discard} onSave={save} />
    </div>
  );
};
